import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ProductoBuscador from '../../../components/ProduccionConcesionaria/ProductoBuscador';
import Toast from '../../../components/Common/Toast';
import recetaPlatoService from '../../../services/recetaPlatoService';
import { formatearCantidadReceta4Dec } from '../../../utils/produccionGuiaCostos';

const InsumoEnRecetas = () => {
  const [productId, setProductId] = useState(null);
  const [productLabel, setProductLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const handleSelectProduct = async ({ productId: pid, label }) => {
    const id = pid != null ? Number(pid) : null;
    setProductId(id);
    setProductLabel(label || '');
    setResult(null);

    if (!id) return;

    setLoading(true);
    try {
      const res = await recetaPlatoService.reporteUsoInsumo(id);
      if (res.success && res.data) {
        setResult(res.data);
      } else {
        setToast({
          isOpen: true,
          message: res.message || 'No se pudo consultar el reporte',
          type: 'error',
        });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al consultar recetas',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const rows = result?.rows || [];

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto w-full">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">
          Dashboard
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion" className="hover:text-teal-600">
          Producción
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Reportes</span>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Uso de insumo en recetas</span>
      </nav>

      <h1 className="text-2xl font-bold text-gray-800">Uso de insumo en recetas</h1>
      <p className="text-gray-600 text-sm mt-1 mb-6 max-w-3xl">
        Seleccione un producto insumo y el sistema listará todas las recetas que lo utilizan, con la cantidad
        definida en cada ficha.
      </p>

      <div className="bg-white rounded-lg shadow border border-gray-200 p-5 sm:p-6 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Producto / insumo <span className="text-red-500">*</span>
        </label>
        <div className="max-w-xl">
          <ProductoBuscador
            mode="insumo"
            value={productLabel}
            productId={productId}
            onSelectProduct={handleSelectProduct}
            placeholder="Buscar insumo por nombre o SKU…"
          />
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-600">
          Consultando recetas…
        </div>
      ) : null}

      {!loading && productId && result ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-teal-200 bg-teal-50/60 px-4 py-3 text-sm text-teal-900">
            <span className="font-medium">{result.product?.name}</span>
            {result.product?.sku ? (
              <span className="ml-2 font-mono text-xs text-teal-800">({result.product.sku})</span>
            ) : null}
            <span className="mx-2 text-teal-600">·</span>
            <span>
              {result.recipes_count === 0
                ? 'No se usa en ninguna receta'
                : `${result.recipes_count} receta${result.recipes_count === 1 ? '' : 's'}`}
            </span>
          </div>

          {rows.length === 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-sm text-amber-900">
              Este insumo no aparece en ninguna ficha de receta activa o registrada.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <th className="px-3 py-2">Receta</th>
                    <th className="px-3 py-2">Clasificación</th>
                    <th className="px-3 py-2 text-right">Cant. neta</th>
                    <th className="px-3 py-2 text-right">% merma</th>
                    <th className="px-3 py-2 text-right">Bruto</th>
                    <th className="px-3 py-2">Unidad</th>
                    <th className="px-3 py-2 text-right">Salida estándar</th>
                    <th className="px-3 py-2 text-center">Estado</th>
                    <th className="px-3 py-2 text-center">Ficha</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row) => {
                    const net = parseFloat(String(row.quantity ?? 0).replace(',', '.')) || 0;
                    const merma = parseFloat(String(row.merma_pct ?? 0).replace(',', '.')) || 0;
                    const bruto = merma > 0 ? net * (1 + merma / 100) : net;
                    return (
                    <tr key={row.receta_plato_id} className="hover:bg-gray-50/80">
                      <td className="px-3 py-2 align-middle">
                        <div className="font-medium text-gray-900">{row.receta_name}</div>
                        {row.receta_code ? (
                          <div className="text-xs text-gray-500 font-mono">{row.receta_code}</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 align-middle text-gray-600 text-xs leading-snug max-w-[220px]">
                        {row.classification}
                      </td>
                      <td className="px-3 py-2 align-middle text-right tabular-nums font-mono">
                        {formatearCantidadReceta4Dec(net)}
                      </td>
                      <td className="px-3 py-2 align-middle text-right tabular-nums font-mono text-amber-800">
                        {merma > 0 ? `${merma}` : '—'}
                      </td>
                      <td className="px-3 py-2 align-middle text-right tabular-nums font-mono font-medium">
                        {formatearCantidadReceta4Dec(bruto)}
                      </td>
                      <td className="px-3 py-2 align-middle text-gray-700">{row.unit_of_measure}</td>
                      <td className="px-3 py-2 align-middle text-right tabular-nums font-mono text-xs text-gray-700 whitespace-nowrap">
                        {row.output_quantity} {row.output_unit_of_measure}
                      </td>
                      <td className="px-3 py-2 align-middle text-center">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            row.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {row.is_active ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-middle text-center">
                        <Link
                          to={`/produccion/clasificacion-recetas/recetas/editar/${row.receta_plato_id}`}
                          className="text-teal-700 hover:underline text-xs font-medium"
                        >
                          Ver
                        </Link>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
};

export default InsumoEnRecetas;
